import { useMemo, useState, type ReactNode } from "react";
import moment from "moment";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar, Copy, EllipsisVertical, Eye, FileText, History, Pencil, Repeat, ClipboardList, Info } from "lucide-react";
import { HoverTooltip } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/use-auth";
import { formatDate, splitIcdCode } from "@/utils/helpers";
import {
  careplanHeaders,
  pcmcareplanHeaders,
  treatmentPlanHeaders,
} from "@/utils/tablesData";
import type { CarePlanV2, PatientV2, TreatmentPlanV2 } from "../types";
import TableRowCardWithSearch from "./TableRowCardWithSearch";
import { CarePlanHistoryDrawer } from "./CarePlanHistoryDrawer";
import { PatientV2ExpandableRowCard } from "./PatientV2ExpandableRowCard";
import {
  formatNursingProgramAcronym,
  formatNursingProgramTitle,
} from "../nursingNotes/nursingNoteConstants";
import {
  patientV2BlueOutlineBadgeClass,
  patientV2GrayBadgeClass,
  patientV2InvalidBadgeClass,
  patientV2SoftBadgeClass,
} from "../constants";
import { ScheduleTelehealthDialog, TelehealthAppointmentsDialog, TelehealthBookedDialog } from "../tcm";
import { extractTcmPlans, tcmPlanCardCopy, type TcmPlanRecord } from "../tcm/tcmApi";

const tcmPlanHeaders = [
  { id: "dischargedOn", label: "Discharged", isSortable: false },
  { id: "from", label: "From", isSortable: false },
  { id: "to", label: "To", isSortable: false },
  { id: "planStatus", label: "Plan Status", isSortable: false },
  { id: "complexity", label: "Complexity", isSortable: false },
  { id: "telehealthStatus", label: "Telehealth", isSortable: false },
];

const EMPTY_FILTERS = {};

function normalizeProgramKey(value: unknown): string {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "ccm" || raw.includes("chronic")) return "ccm";
  if (raw === "pcm" || raw.includes("principal")) return "pcm";
  if (raw === "rpm" || raw.includes("remote")) return "rpm";
  return raw;
}

function resolveProgramCardHeader(planValue: unknown) {
  const programKey = normalizeProgramKey(planValue);
  const title = formatNursingProgramTitle(programKey) || formatCarePlanTypeLabel(planValue);
  const acronym =
    formatNursingProgramAcronym(programKey) ||
    (programKey ? programKey.toUpperCase() : formatCarePlanTypeLabel(planValue));
  return { title, acronym };
}

function formatCarePlanTypeLabel(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "—") return "Care Plan";
  return raw.toUpperCase();
}

function resolveCarePlanType(row: Record<string, unknown> | null | undefined): string {
  return String(row?.carePlanType ?? row?.plan ?? row?.planType ?? row?.type ?? "")
    .trim()
    .toLowerCase();
}

function buildCreateFollowUpUrl(patientId: string, row: CarePlanV2, isPcmOnly: boolean): string {
  const planType = resolveCarePlanType(row as Record<string, unknown>);
  const usePcm = planType === "pcm" || (isPcmOnly && !planType);
  return usePcm
    ? `/careplan/${patientId}?type=followup&plan=pcm`
    : `/careplan/${patientId}?type=followup`;
}

function buildDuplicateCarePlanUrl(
  patientId: string,
  programs: string[] | undefined,
): string {
  const usePcm =
    Array.isArray(programs) &&
    programs.includes("pcm") &&
    !programs.includes("ccm");
  return usePcm
    ? `/careplan/${patientId}?type=duplicate&plan=pcm`
    : `/careplan/${patientId}?type=duplicate`;
}

function isCarePlanRowInvalid(row: CarePlanV2 | Record<string, unknown>): boolean {
  return !!(row as { invalid?: boolean })?.invalid;
}

function resolveCaretakerName(name: string): string {
  return String(name || "Unassigned").trim() || "Unassigned";
}

function extractIcdCodeInitial(code: unknown): string {
  const raw = String(code ?? "").trim();
  if (!raw || raw === "—") return "";

  if (raw.includes("/")) {
    return raw.split("/")[0]?.trim() || raw;
  }

  return splitIcdCode(raw)[0] || raw;
}

function formatIcdCodesInitials(codes: unknown): string {
  const row =
    codes == null
      ? {}
      : Array.isArray(codes)
        ? { icdCodes: codes }
        : { icdCode: codes };
  const list = normalizeIcdCodeLines(row);
  const initials = list.map(extractIcdCodeInitial).filter(Boolean);
  return [...new Set(initials)].join(", ");
}

function normalizeIcdCodeLines(row: Record<string, unknown> | null | undefined): string[] {
  if (!row) return [];

  const fromList = (entries: unknown[]): string[] =>
    entries
      .map((entry) => {
        if (entry == null) return "";
        if (typeof entry === "object") {
          const record = entry as Record<string, unknown>;
          const code = String(record.code ?? record.icdCode ?? "").trim();
          const description = String(record.description ?? record.name ?? "").trim();
          if (code && description) return `${code}-${description}`;
          return code || description;
        }
        return String(entry).trim();
      })
      .filter((entry) => entry && entry !== "—");

  const raw = row.icdCodes;
  if (Array.isArray(raw) && raw.length > 0) {
    return fromList(raw);
  }

  if (raw != null && String(raw).trim() && String(raw) !== "—") {
    return String(raw)
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry && entry !== "—");
  }

  const single = row.icdCode;
  if (single != null && String(single).trim() && String(single) !== "—") {
    const value = String(single).trim();
    return value.includes(",")
      ? value
          .split(",")
          .map((entry) => entry.trim())
          .filter(Boolean)
      : [value];
  }

  return [];
}

function IcdCodeChip({ line }: { line: string }) {
  const raw = String(line).trim();
  const [code, description] = splitIcdCode(raw);
  const codeLabel = code || raw;
  const tooltipContent = description ? (
    <div className="max-w-xs text-sm leading-snug">
      <span className="font-semibold">{codeLabel}</span>
      <span className="text-muted-foreground"> — </span>
      <span>{description}</span>
    </div>
  ) : (
    <span className="text-sm font-semibold">{codeLabel}</span>
  );

  return (
    <HoverTooltip content={tooltipContent} side="top">
      <div className="inline-flex max-w-full min-w-0 cursor-pointer items-center rounded-full border border-[#E5E7EB] bg-[#F9FAFB] py-0.5 pl-0.5 pr-2">
        <span className="shrink-0 rounded-full bg-[#E5E7EB] px-2 py-0.5 text-xs font-semibold text-[#111827]">
          {codeLabel}
        </span>
        {description ? (
          <span className="ml-1.5 min-w-0 max-w-none whitespace-normal text-xs font-medium text-[#111827]">
            {description}
          </span>
        ) : null}
      </div>
    </HoverTooltip>
  );
}

function formatCardDate(value: unknown) {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("MM-DD-YYYY") : raw;
}

function CardDateMeta({ createdAt, updatedAt }: { createdAt: string; updatedAt: string }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
      <span>
        <span className="font-semibold uppercase tracking-wide">Created:</span>{" "}
        <span className="text-foreground">{createdAt}</span>
      </span>
      <span>
        <span className="font-semibold uppercase tracking-wide">Updated:</span>{" "}
        <span className="text-foreground">{updatedAt}</span>
      </span>
    </div>
  );
}

function PlanCaretakerHeader({
  programTag,
  caretaker,
  isInvalid,
  status,
}: {
  programTag: string;
  caretaker: string;
  isInvalid?: string;
  status?: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[14px] leading-snug">
      <span className="min-w-0 truncate text-muted-foreground">
        <span className="font-semibold text-foreground/80">Caretaker:</span>{" "}
        <span className="text-foreground">{resolveCaretakerName(caretaker)}</span>
      </span>
      <Badge className={`${patientV2SoftBadgeClass} shrink-0 uppercase`}>{programTag}</Badge>
      {status ? (
        <Badge className={`${patientV2GrayBadgeClass} shrink-0 uppercase`}>{status}</Badge>
      ) : null}
      {isInvalid ? (
        <Badge className={`${patientV2InvalidBadgeClass} shrink-0 uppercase`}>{isInvalid}</Badge>
      ) : null}
    </div>
  );
}

function formatDeviceTypeLabel(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => formatDeviceTypeLabel(item))
      .filter((item) => item !== "—");
    return formatted.length ? formatted.join(", ") : "—";
  }
  const raw = String(value).trim();
  if (!raw || raw === "—") return "—";
  return raw.replace(/_/g, " ");
}

export function CarePlanTab({
  patient,
  patientId,
  carePlans,
  treatmentPlans,
  activeSubTab,
}: {
  patient: PatientV2 | null;
  patientId: string;
  carePlans: CarePlanV2[];
  treatmentPlans: TreatmentPlanV2[];
  activeSubTab?: string;
  onSubTabChange?: (tab: string) => void;
}) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [carePlanRows, setCarePlanRows] = useState<CarePlanV2[]>(carePlans || []);
  const [treatmentPlanRows, setTreatmentPlanRows] = useState<TreatmentPlanV2[]>(
    treatmentPlans || [],
  );
  const [planHistory, setPlanHistory] = useState<Record<string, unknown> | null>(null);
  const [isPlanHistoryOpen, setIsPlanHistoryOpen] = useState(false);
  const [scheduleTelehealthOpen, setScheduleTelehealthOpen] = useState(false);
  const [schedulePlanId, setSchedulePlanId] = useState("");
  const [telehealthBookedOpen, setTelehealthBookedOpen] = useState(false);
  const [bookedMeetingLink, setBookedMeetingLink] = useState("");
  const [telehealthDetailsOpen, setTelehealthDetailsOpen] = useState(false);
  const [tcmPlanRows, setTcmPlanRows] = useState<TcmPlanRecord[]>([]);

  const isPcmOnly =
    Array.isArray(patient?.programs) &&
    patient.programs.includes("pcm") &&
    !patient.programs.includes("ccm");

  const activeCarePlanHeaders =
    Array.isArray(patient?.programs) && patient.programs.includes("pcm")
      ? pcmcareplanHeaders
      : careplanHeaders;

  const carePlanUrl = (isPcmOnly && false) ? "/pcm-careplan" : "/careplan";
  const listParams = useMemo(() => ({ patientId }), [patientId]);

  const userRole = String((user as { role?: string } | null)?.role ?? "").toLowerCase();
  const managerScope = String((user as { managerScope?: string } | null)?.managerScope ?? "");
  const isProviderOrPractitioner = userRole === "provider" || userRole === "practitioner";
  const isProviderOrPractitionerManager =
    userRole === "manager" && (managerScope === "provider" || managerScope === "practitioner");
  const isCaretaker = userRole === "caretaker";
  const profileId = String((profile as { _id?: string } | null)?._id ?? "");
  const assignedCaretakerIdList = (patient?.assignedCaretakerIds ?? []).map((caretaker) =>
    typeof caretaker === "string" ? caretaker : caretaker?._id,
  );
  const isCaretakerAssignedToPatient =
    !isCaretaker || (!!profileId && assignedCaretakerIdList.includes(profileId));
  const canShowCarePlanActions =
    !isProviderOrPractitioner &&
    !isProviderOrPractitionerManager &&
    (!isCaretaker || isCaretakerAssignedToPatient);
  const topCarePlanId = String((carePlanRows?.[0] as { _id?: string } | undefined)?._id ?? "");

  const carePlanViewData = useMemo(
    () =>
      (carePlanRows || []).map((v: any) => ({
        ...v,
        careTakerId: [v?.careTakerId?.firstName, v?.careTakerId?.middleName, v?.careTakerId?.lastName].filter(Boolean).join(" "),
        icdCodes: (() => {
          const lines = normalizeIcdCodeLines(v);
          return lines.length ? lines.join(", ") : "—";
        })(),
        icdCodesInitials: formatIcdCodesInitials(v?.icdCodes ?? v?.icdCode),
        createdAt: v?.createdAt ? formatDate(v.createdAt) || "—" : "—",
        updatedAt: v?.updatedAt ? formatDate(v.updatedAt) || "—" : "—",
        plan: formatCarePlanTypeLabel(
          v?.carePlanType || v?.plan || v?.planType || v?.type,
        ),
        status: v?.status?.replace?.(/_/g, " ") ?? v?.status ?? "—",
        isInvalid: v?.invalid ? 'Invalid' : '',
      })),
    [carePlanRows],
  );

  const treatmentPlanViewData = useMemo(
    () =>
      (treatmentPlanRows || []).map((v: any) => ({
        ...v,
        careTakerId: [v?.careTakerId?.firstName, v?.careTakerId?.middleName, v?.careTakerId?.lastName].filter(Boolean).join(" "),
        deviceType: formatDeviceTypeLabel(v?.deviceType),
        status: v?.status?.replace?.(/_/g, " ") ?? v?.status ?? "—",
        isInvalid: v?.invalid ? 'Invalid' : '',
        createdAt: v?.createdAt ? formatDate(v.createdAt) || "—" : "—",
        updatedAt: v?.updatedAt ? formatDate(v.updatedAt) || "—" : "—",
      })),
    [treatmentPlanRows],
  );

  const subTab = activeSubTab ?? "care-plan";
  const tcmPlanViewData = useMemo(
    () =>
      (tcmPlanRows || []).map((plan) => ({
        ...plan,
        ...tcmPlanCardCopy(plan),
      })),
    [tcmPlanRows],
  );

  const renderCardActions = (
    actions: Array<{
      label: string;
      icon: ReactNode;
      onClick?: (row: unknown) => void;
    }>,
    row: unknown,
    onPrimaryAction?: () => void,
  ) => {
    if (!onPrimaryAction && actions.length === 0) return null;

    return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 shrink-0 p-0 !bg-transparent  hover:!bg-transparent hover:text-[#111827] focus:!bg-transparent data-[state=open]:!bg-transparent"          
          onClick={(e) => e.stopPropagation()}
          title="More actions"
        >
          <EllipsisVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {onPrimaryAction ? (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              onPrimaryAction();
            }}
          >
            <Pencil className="h-4 w-4" />
            Edit
          </DropdownMenuItem>
        ) : null}
        {actions.map((action, idx) => (
          <DropdownMenuItem
            key={`${action.label}-${idx}`}
            onSelect={(e) => {
              e.preventDefault();
              action.onClick?.(row);
            }}
          >
            {action.icon}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
    );
  };

  return (
    <>
      {subTab === "tcm-plan" ? (
        <TableRowCardWithSearch
          heading="TCM Plans"
          url="/tcm-plans"
          params={{ patientId }}
          pageAndLimitOnly
          apiDataKey=""
          fetchCondition={!!patientId}
          isIncludeSearch={false}
          isIgnoreSearchParams
          searchPlaceholder="Search TCM plans..."
          columnHeaders={tcmPlanHeaders}
          data={tcmPlanRows}
          viewData={tcmPlanViewData}
          setData={setTcmPlanRows}
          isActionColumn={false}
          handleAction={() => {}}
          resolveItems={(payload) => extractTcmPlans(payload)}
          moreActions={canShowCarePlanActions ? [
            {
              label: "Edit TCM plan",
              icon: <Pencil className="h-4 w-4" />,
              isShow: (plan: TcmPlanRecord) => canShowCarePlanActions && !!plan?.id,
              onClick: (plan: TcmPlanRecord) => {
                navigate(`/tcm-plan/${patientId}/${plan.id}`, {
                  state: { patientData: patient },
                });
              },
            },
            {
              label: "Schedule Telehealth",
              icon: <Calendar className="h-4 w-4" />,
              isShow: (plan: TcmPlanRecord) => canShowCarePlanActions && !!plan?.id,
              onClick: (plan: TcmPlanRecord) => {
                setSchedulePlanId(plan.id);
                setScheduleTelehealthOpen(true);
              },
            },
            // {
            //   label: "Telehealth Details",
            //   icon: <Info className="h-4 w-4" />,
            //   isShow: () => true,
            //   onClick: () => setTelehealthDetailsOpen(true),
            // },
          ] : []}
          filtersOptions={[]}
          filters={EMPTY_FILTERS}
          setFilters={() => {}}
          clickRow={() => {}}
          renderRowCard={({ viewRow, row, actions }) => {
            const card = viewRow as ReturnType<typeof tcmPlanCardCopy>;
            return (
              <PatientV2ExpandableRowCard
                icon={<ClipboardList className="h-5 w-5 text-muted-foreground" />}
                subtitle={
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-semibold leading-snug text-[#111827]">
                      Discharged: {card.dischargedOn}
                    </p>
                    <span className="inline-flex max-w-full flex-wrap items-center gap-x-1 rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-2.5 py-1 text-xs text-[#6B7280]">
                      <span className="font-semibold uppercase">From:</span>
                      <span className="text-[#111827]">{card.from}</span>
                      <span aria-hidden>→</span>
                      <span className="font-semibold uppercase">To:</span>
                      <span className="text-[#111827]">{card.to}</span>
                    </span>
                  </div>
                }
                headerMeta={
                  <div className="flex flex-wrap gap-2">
                    <Badge className={`${patientV2BlueOutlineBadgeClass} rounded-full normal-case`}>
                      Plan Status: <span className="font-semibold">{card.planStatus}</span>
                    </Badge>
                    <Badge className={`${patientV2GrayBadgeClass} rounded-full normal-case`}>
                      Complexity: <span className="font-semibold">{card.complexity}</span>
                    </Badge>
                    <Badge className="rounded-full border-[#E9D5FF] bg-[#FAF5FF] text-xs font-medium text-[#7E22CE] normal-case">
                      Telehealth: <span className="font-semibold">{card.telehealthStatus}</span>
                      {card.telehealthAt ? (
                        <span className="text-[#A855F7]"> • {card.telehealthAt}</span>
                      ) : null}
                    </Badge>
                  </div>
                }
                details={[]}
                actions={renderCardActions(actions as any[], row)}
              />
            );
          }}
        />
      ) : subTab === "care-plan" ? (
        <TableRowCardWithSearch
          heading="Care Plans"
          url={carePlanUrl}
          params={listParams}
          apiDataKey="carePlan"
          fetchCondition={!!patientId}
          isIncludeSearch={false}
          isIgnoreSearchParams
          searchPlaceholder="Search care plans..."
          columnHeaders={activeCarePlanHeaders}
          data={carePlanRows as any[]}
          viewData={carePlanViewData}
          setData={setCarePlanRows}
          isActionColumn={canShowCarePlanActions}
          handleAction={(data: CarePlanV2) => {
            if (!canShowCarePlanActions || !(data as any)?._id) return;
            const suffix = isPcmOnly ? "?plan=pcm" : "";
            navigate(`/careplan/${patientId}/${(data as any)?._id}${suffix}`, {
              state: { patientData: patient, carePlan: data },
            });
          }}
          moreActions={[
            {
              label: "View",
              icon: <Eye className="h-4 w-4" />,
              isShow: () => true,
              onClick: (data: CarePlanV2) => {
                const suffix = isPcmOnly ? "?plan=pcm" : "";
                navigate(`/patient-careplan/${(data as any)?._id}${suffix}`, {
                  state: { patientData: patient, carePlan: data },
                });
              },
            },
            {
              label: "Create Follow up",
              icon: <Repeat className="h-4 w-4" />,
              isShow: (rowData: CarePlanV2) =>
                canShowCarePlanActions && !isCarePlanRowInvalid(rowData) &&
                String(patient?.lastCarePlanStatus ?? "").trim().toLowerCase() === "followup" &&
                !!rowData?._id &&
                String(rowData._id) === topCarePlanId,
              onClick: (data: CarePlanV2) => {
                navigate(buildCreateFollowUpUrl(patientId, data, isPcmOnly), {
                  state: { patientData: patient, carePlan: data },
                });
              },
            },
            {
              label: "Convert Care Plan",
              icon: <Copy className="h-4 w-4" />,
              isShow: (rowData: CarePlanV2) =>
                canShowCarePlanActions &&
                isCarePlanRowInvalid(rowData) &&
                !!rowData?._id,
              onClick: (data: CarePlanV2) => {
                navigate(buildDuplicateCarePlanUrl(patientId, patient?.programs), {
                  state: { patientData: patient, carePlan: data },
                });
              },
            },
            {
              label: "History",
              icon: <History className="h-4 w-4" />,
              isShow: (data: CarePlanV2) =>
                Array.isArray((data as any)?.editHistory) &&
                (data as any).editHistory.length > 0,
              onClick: (data: CarePlanV2) => {
                setPlanHistory(data as Record<string, unknown>);
                setIsPlanHistoryOpen(true);
              },
            },
          ]}
          filtersOptions={[]}
          filters={EMPTY_FILTERS}
          setFilters={() => {}}
          clickRow={() => {}}
          renderRowCard={({ viewRow, row, actions, onPrimaryAction }) => {
            const v = viewRow as Record<string, unknown>;
            const rowData = row as Record<string, unknown>;
            const caretaker = String(v?.careTakerId || "Unassigned").trim();
            const planValue = v?.plan ?? rowData?.carePlanType;
            const { acronym: programAcronym } = resolveProgramCardHeader(planValue);
            const icdLines = normalizeIcdCodeLines(rowData);
            const createdAt = formatCardDate(v?.createdAtLocal ?? v?.createdAt);
            const updatedAt = formatCardDate(v?.updatedAtLocal ?? v?.updatedAt);
            const isInvalid = String(v?.isInvalid || "").trim();
            const status = String(v?.status || "—");
            return (
              <PatientV2ExpandableRowCard
                icon={<FileText className="h-5 w-5 text-muted-foreground" />}
                subtitle={
                  <PlanCaretakerHeader
                    programTag={programAcronym || "Care Plan"}
                    caretaker={caretaker}
                    isInvalid={isInvalid}
                    status={status}
                  />
                }
                headerMeta={<CardDateMeta createdAt={createdAt} updatedAt={updatedAt} />}
                details={[
                  {
                    label: "ICD Codes",
                    fullWidth: true,
                    renderValue:
                      icdLines.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {icdLines.map((line, idx) => (
                            <IcdCodeChip key={`${line}-${idx}`} line={line} />
                          ))}
                        </div>
                      ) : (
                        <p className="text-[14px] leading-snug text-foreground">—</p>
                      ),
                  },
                ]}
                actions={renderCardActions(actions as any[], row, onPrimaryAction)}
              />
            );
          }}
        />
      ) : (
        <TableRowCardWithSearch
          heading="Treatment Plans"
          url="/treatment-plan"
          params={listParams}
          apiDataKey="treatmentPlan"
          fetchCondition={!!patientId}
          isIncludeSearch={false}
          isIgnoreSearchParams
          searchPlaceholder="Search treatment plans..."
          columnHeaders={treatmentPlanHeaders}
          data={treatmentPlanRows as any[]}
          viewData={treatmentPlanViewData}
          setData={setTreatmentPlanRows}
          isActionColumn={false}
          handleAction={() => {}}
          moreActions={[
            {
              label: "Edit",
              icon: <Pencil className="h-4 w-4" />,
              isShow: (data: TreatmentPlanV2) =>
                canShowCarePlanActions && !!(patientId && (data as any)?._id),
              onClick: (data: TreatmentPlanV2) => {
                navigate(`/treatment-plan/${patientId}/${(data as any)?._id}`, {
                  state: { patientData: patient, carePlan: data },
                });
              },
            },
            {
              label: "View",
              icon: <Eye className="h-4 w-4" />,
              isShow: (data: TreatmentPlanV2) => !!(patientId && (data as any)?._id),
              onClick: (data: TreatmentPlanV2) => {
                navigate(`/treatment-plan/${patientId}/${(data as any)?._id}?view=true`, {
                  state: { patientData: patient, carePlan: data },
                });
              },
            },
            {
              label: "History",
              icon: <History className="h-4 w-4" />,
              isShow: (data: TreatmentPlanV2) =>
                Array.isArray((data as any)?.editHistory) &&
                (data as any).editHistory.length > 0,
              onClick: (data: TreatmentPlanV2) => {
                setPlanHistory(data as Record<string, unknown>);
                setIsPlanHistoryOpen(true);
              },
            },
          ]}
          filtersOptions={[]}
          filters={EMPTY_FILTERS}
          setFilters={() => {}}
          clickRow={() => {}}
          renderRowCard={({ viewRow, row, actions, onPrimaryAction }) => {
            const v = viewRow as Record<string, unknown>;
            const caretaker = String(v?.careTakerId || "Unassigned").trim();
            const deviceType = String(v?.deviceType || "—");
            const status = String(v?.status || "—");
            const { acronym: programAcronym } = resolveProgramCardHeader("rpm");
            const createdAt = formatCardDate(v?.createdAtLocal ?? v?.createdAt);
            const updatedAt = formatCardDate(v?.updatedAtLocal ?? v?.updatedAt);
            const isInvalid = String(v?.isInvalid || "").trim();

            return (
              <PatientV2ExpandableRowCard
                icon={<ClipboardList className="h-5 w-5 text-muted-foreground" />}
                subtitle={
                  <PlanCaretakerHeader
                    programTag={programAcronym || "RPM"}
                    caretaker={caretaker}
                    isInvalid={isInvalid}
                    status={status}
                  />
                }
                headerMeta={<CardDateMeta createdAt={createdAt} updatedAt={updatedAt} />}
                details={[
                  { label: "Device Type", value: deviceType },
                  { label: "Status", value: status },
                ]}
                actions={renderCardActions(actions as any[], row, onPrimaryAction)}
              />
            );
          }}
        />
      )}

      <ScheduleTelehealthDialog
        open={scheduleTelehealthOpen}
        onOpenChange={(open) => {
          setScheduleTelehealthOpen(open);
          if (!open) setSchedulePlanId("");
        }}
        patient={patient}
        patientId={patient?._id || patientId}
        tcmPlanId={schedulePlanId}
        onScheduled={({ meetingLink }) => {
          setBookedMeetingLink(meetingLink);
          setTelehealthBookedOpen(true);
        }}
      />
      <TelehealthBookedDialog
        open={telehealthBookedOpen}
        onOpenChange={(open) => {
          setTelehealthBookedOpen(open);
          if (!open) setBookedMeetingLink("");
        }}
        meetingLink={bookedMeetingLink}
      />
      <TelehealthAppointmentsDialog
        open={telehealthDetailsOpen}
        onOpenChange={setTelehealthDetailsOpen}
        patient={patient}
        patientId={patient?._id || patientId}
      />

      <CarePlanHistoryDrawer
        open={isPlanHistoryOpen}
        onOpenChange={(open) => {
          setIsPlanHistoryOpen(open);
          if (!open) setPlanHistory(null);
        }}
        planHistory={planHistory}
      />
    </>
  );
}
